/**
 * Home Screen Editor
 * 
 * Allows caregivers to edit home screen items (icon, label)
 * with undo support and drag-to-reorder functionality.
 * Supports both flower (cross) and row layouts for editing.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { motion } from "framer-motion";
import { Pencil, Check, Undo2, RotateCcw, GripVertical, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useCaregiverTranslation } from "@/hooks/useCaregiverTranslation";
import { BoardItemEditForm } from "@/components/communication/BoardItemEditForm";
import { ICON_MAP } from "@/components/ui/LucideIconPicker";
import { getNavigationColorClasses } from "@/components/communication/NavigationColorPicker";

import { getUserSettings } from "@/lib/userSettings";
import { useTranslation } from "@/hooks/useTranslation";
import {
  NavigationBoardData,
  NavigationItem,
  loadNavigationBoard,
  saveNavigationBoard,
  updateNavigationItem,
  reorderNavigationItems,
  resetNavigationBoard,
  getDefaultItems,
} from "@/lib/navigationBoardTypes";

const MAX_UNDO_HISTORY = 20;

type LayoutMode = "row" | "flower";

/**
 * Determine initial layout mode based on user's control settings
 * - Flower: 3+ gestures with gesture-controlled scanning
 * - Row: 1-2 gestures OR automatic scanning
 */
function getInitialLayoutMode(): LayoutMode {
  const settings = getUserSettings();
  const controlMethod = settings.controlMethod;
  const isEyeControl = controlMethod === 'eye_control' || controlMethod === 'eyecontrol';
  const gestureCount = settings.eyeControlGestures?.length || 0;
  const isAutoScan = settings.scanControlMode === 'automatic';
  
  // Row layout for: 1-2 gestures OR automatic scanning
  if (isEyeControl && (gestureCount <= 2 || isAutoScan)) {
    return 'row';
  }
  
  // Flower layout for: 3+ gestures with gesture control OR any non-EyeControl method
  return 'flower';
}

export default function HomeEditor() {
  const navigate = useNavigate();
  const { t, dir, isRTL } = useCaregiverTranslation();
  const { isRTL: isUserRTL } = useTranslation();
  
  const [board, setBoard] = useState<NavigationBoardData>(() => loadNavigationBoard("home"));
  const [editingItem, setEditingItem] = useState<NavigationItem | null>(null);
  const [undoHistory, setUndoHistory] = useState<NavigationBoardData[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [layoutMode, setLayoutMode] = useState<LayoutMode>(getInitialLayoutMode);
  const isUndoing = useRef(false);

  // Safety: HTML5 drag events can sometimes miss onDragEnd (esp. after quick clicks).
  // Clear drag visual state on any drag/mouse/touch end so no other tile stays dim.
  useEffect(() => {
    const clearDragState = () => setDraggedIndex(null);
    window.addEventListener("dragend", clearDragState);
    window.addEventListener("drop", clearDragState);
    window.addEventListener("mouseup", clearDragState);
    window.addEventListener("touchend", clearDragState);
    return () => {
      window.removeEventListener("dragend", clearDragState);
      window.removeEventListener("drop", clearDragState);
      window.removeEventListener("mouseup", clearDragState);
      window.removeEventListener("touchend", clearDragState);
    };
  }, []);

  // Save to localStorage when board changes
  useEffect(() => {
    if (!isUndoing.current) {
      saveNavigationBoard(board);
    }
  }, [board]);

  // Save to undo history before making changes
  const saveToHistory = useCallback(() => {
    setUndoHistory(prev => {
      const newHistory = [...prev, board];
      if (newHistory.length > MAX_UNDO_HISTORY) {
        return newHistory.slice(-MAX_UNDO_HISTORY);
      }
      return newHistory;
    });
  }, [board]);

  // Undo last action
  const handleUndo = useCallback(() => {
    if (undoHistory.length === 0) return;
    
    isUndoing.current = true;
    const previousState = undoHistory[undoHistory.length - 1];
    setUndoHistory(prev => prev.slice(0, -1));
    setBoard(previousState);
    saveNavigationBoard(previousState);
    setDraggedIndex(null);
    
    setTimeout(() => {
      isUndoing.current = false;
    }, 0);
  }, [undoHistory]);

  // Get label in user's language for a navigation item
  const getUserLangLabel = (item: NavigationItem): string => {
    const userLang = getUserSettings().language || "he";
    if (userLang === "ar") return item.labelAr || item.labelEn;
    if (userLang === "ru") return item.labelRu || item.labelEn;
    if (userLang === "he") return item.labelHe;
    return item.labelEn;
  };

  // Get the update object targeting the user's language field
  const getUserLangUpdate = (name: string): Partial<NavigationItem> => {
    const userLang = getUserSettings().language || "he";
    if (userLang === "ar") return { labelAr: name };
    if (userLang === "ru") return { labelRu: name };
    if (userLang === "he") return { labelHe: name };
    return { labelEn: name };
  };

  // Handle save from edit form
  const handleSaveEdit = (data: { name: string; emoji: string; color: string; showEmoji: boolean; iconName?: string }) => {
    if (!editingItem) return;
    saveToHistory();
    
    const updatedBoard = updateNavigationItem(board, editingItem.id, {
      ...getUserLangUpdate(data.name),
      iconName: data.iconName || editingItem.iconName,
      color: data.color,
    });
    setBoard(updatedBoard);
    setEditingItem(null);
  };

  // Handle reset to defaults
  const handleResetToDefault = () => {
    saveToHistory();
    const defaultBoard = resetNavigationBoard("home");
    setBoard(defaultBoard);
    setDraggedIndex(null);
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    saveToHistory();
    const newBoard = reorderNavigationItems(board, draggedIndex, index);
    setBoard(newBoard);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // If editing, show the edit form
  if (editingItem) {
    return (
      <BoardItemEditForm
        type="navigation"
        initialName={getUserLangLabel(editingItem)}
        initialEmoji=""
        initialColor={editingItem.color as any}
        initialIconName={editingItem.iconName}
        iconMode="lucide"
        iconOnly
        onSave={handleSaveEdit}
        onCancel={() => setEditingItem(null)}
      />
    );
  }

  // Get sorted items
  const sortedItems = [...board.items].sort((a, b) => a.order - b.order);

  // Handle visibility toggle
  const handleVisibilityToggle = (itemId: string, visible: boolean) => {
    setDraggedIndex(null);
    saveToHistory();
    const updatedBoard = updateNavigationItem(board, itemId, { visible });
    setBoard(updatedBoard);
  };

  // Render a single editable item
  // Light/dark mode custom color classes per item key (matches HomeGrid)
  // Only apply hardcoded CSS class when item still has its default color
  const defaultItems = getDefaultItems("home");
  const HOME_CARD_CLASSES: Record<string, string> = {
    communication: 'home-card-communication',
    keyboard: 'home-card-keyboard',
    entertainment: 'home-card-entertainment',
    rest: 'home-card-rest',
    help: 'home-card-help',
  };
  const getHomeCardClass = (item: NavigationItem) => {
    const defaultColor = defaultItems.find(d => d.key === item.key)?.color;
    if (defaultColor && item.color === defaultColor) {
      return HOME_CARD_CLASSES[item.key] || '';
    }
    return '';
  };

  const renderItem = (item: NavigationItem, index: number, className?: string) => {
    const IconComponent = ICON_MAP[item.iconName] || ICON_MAP.Home;
    const userLang = getUserSettings().language || "he";
    const label = userLang === "ar" ? (item.labelAr || item.labelEn) 
                : userLang === "ru" ? (item.labelRu || item.labelEn)
                : userLang === "he" ? item.labelHe 
                : item.labelEn;
    const isVisible = item.visible === false ? false : true;
    const colorClasses = getNavigationColorClasses(item.color);
    const isCustomColor = !getHomeCardClass(item);
    // Inline style for custom colors — needed to override !important CSS and element override inline styles
    const customColorStyle: React.CSSProperties = {};
    if (!isVisible) { customColorStyle.opacity = 0.4; customColorStyle.filter = 'grayscale(1)'; }
    if (isCustomColor && colorClasses.hex) { customColorStyle.borderColor = colorClasses.hex; }
    
    return (
      <motion.div
        key={`${item.id}-${item.visible}-${item.color}`}
        draggable
        onDragStart={() => handleDragStart(index)}
        onDragOver={(e) => handleDragOver(e, index)}
        onDragEnd={handleDragEnd}
        className={cn(
          "relative aspect-square rounded-2xl border-2 flex flex-col items-center justify-center gap-2",
          "transition-all duration-200 cursor-move",
          "bg-card shadow-md",
          colorClasses.border,
          getHomeCardClass(item),
          draggedIndex === index && "opacity-50 scale-95",
          className
        )}
        style={Object.keys(customColorStyle).length ? customColorStyle : undefined}
        data-design-id={isCustomColor ? undefined : `home-card-${item.id}`}
        whileHover={{ scale: 1.02 }}
      >
        {/* Drag handle */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2">
          <GripVertical className="w-5 h-5 text-muted-foreground" />
        </div>
        
        {/* Edit button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setDraggedIndex(null);
            setEditingItem(item);
          }}
          className={cn(
            "absolute top-3 p-2 bg-primary rounded-full hover:bg-primary/80 z-10",
            isRTL ? "right-3" : "left-3"
          )}
        >
          <Pencil className="w-5 h-5 text-primary-foreground" />
        </button>
        
        {/* Visibility toggle button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setDraggedIndex(null);
            handleVisibilityToggle(item.id, !isVisible);
          }}
          className={cn(
            "absolute top-3 p-2 rounded-full z-10 transition-colors",
            isRTL ? "left-3" : "right-3",
            isVisible 
              ? "bg-accent hover:bg-accent/80" 
              : "bg-muted-foreground/60 hover:bg-muted-foreground/80"
          )}
        >
          {isVisible ? (
            <Eye className="w-5 h-5 text-white" />
          ) : (
            <EyeOff className="w-5 h-5 text-white" />
          )}
        </button>
        
        <IconComponent 
          className={cn(colorClasses.iconColor, getHomeCardClass(item) ? "text-primary" : "")}
          strokeWidth={2}
          style={{ 
            width: 'clamp(2.5rem, 10vh, 5rem)', 
            height: 'clamp(2.5rem, 10vh, 5rem)',
            ...(isCustomColor && colorClasses.hex ? { color: colorClasses.hex } : {}),
          }}
        />
        <span 
          dir="auto" 
          className="font-semibold text-card-foreground text-center px-2 leading-tight"
          style={{ fontSize: 'clamp(1rem, 2.8vh, 1.5rem)' }}
        >
          {label}
        </span>
      </motion.div>
    );
  };

  // Render row layout (5 columns) - larger items
  // In Hebrew (RTL): Help is rightmost, Entertainment is leftmost
  // In English (LTR): Mirror - Help is leftmost, Entertainment is rightmost
  const renderRowLayout = () => {
    const displayItems = isRTL ? sortedItems : [...sortedItems].reverse();
    return (
      <div 
        className="grid grid-cols-5 gap-4 w-full max-w-5xl mx-auto"
        style={{ height: 'min(180px, 25vh)' }}
      >
        {displayItems.map((item, index) => renderItem(item, isRTL ? index : 4 - index, "h-full"))}
      </div>
    );
  };

  // Render flower (cross) layout
  // Flower mapping: Center=0 (Help), Right=1 (Rest), Top=2 (Communication), Left=3 (Keyboard), Bottom=4 (Entertainment)
  // In Hebrew: Rest on right, Keyboard on left
  // In English: Mirrored - Rest on left, Keyboard on right
  const renderFlowerLayout = () => {
    const centerItem = sortedItems[0]; // order 0 = Help = center
    const restItem = sortedItems[1];   // order 1 = Rest
    const topItem = sortedItems[2];    // order 2 = Communication = top
    const keyboardItem = sortedItems[3]; // order 3 = Keyboard
    const bottomItem = sortedItems[4]; // order 4 = Entertainment = bottom

    if (!centerItem || !topItem || !keyboardItem || !restItem || !bottomItem) {
      return renderRowLayout();
    }

    // Mirror positions based on language
    const rightItem = isRTL ? restItem : keyboardItem;
    const leftItem = isRTL ? keyboardItem : restItem;
    const rightIndex = isRTL ? 1 : 3;
    const leftIndex = isRTL ? 3 : 1;

    return (
      <div 
        dir="ltr"
        className="grid grid-cols-3 grid-rows-3 gap-2"
        style={{ 
          aspectRatio: '1/1',
          height: 'min(100%, calc(100vw - 6rem))',
          maxHeight: 'calc(100vh - 7rem)',
          width: 'auto',
        }}
      >
        {/* Top row - Communication in center column */}
        <div className="col-start-2 row-start-1">
          {renderItem(topItem, 2)}
        </div>
        
        {/* Middle row - positions swap based on language */}
        <div className="col-start-3 row-start-2">
          {renderItem(rightItem, rightIndex)}
        </div>
        
        <div className="col-start-2 row-start-2">
          {renderItem(centerItem, 0)}
        </div>
        
        <div className="col-start-1 row-start-2">
          {renderItem(leftItem, leftIndex)}
        </div>
        
        {/* Bottom row - Entertainment in center column */}
        <div className="col-start-2 row-start-3">
          {renderItem(bottomItem, 4)}
        </div>
      </div>
    );
  };

  return (
    <MainLayout onBack={() => navigate(-1)}>
      <div className="flex-1 flex flex-col overflow-hidden bg-secondary dark:bg-background" dir={dir}>
        {/* Header with toggle integrated */}
        <div className="flex items-center justify-between px-4 py-1.5 bg-card/50 border-b border-border">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold text-foreground">
              {isRTL ? "עריכת מסך הבית" : "Edit Home Screen"}
            </h1>
            
            {/* Layout mode toggle - inline with header */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {isRTL ? "מצב סריקה:" : "Scan Mode:"}
              </span>
              <div className="flex rounded-lg overflow-hidden border border-border">
                <button
                  onClick={() => setLayoutMode("row")}
                  className={cn(
                    "px-3 py-1.5 text-sm font-medium transition-colors",
                    layoutMode === "row"
                      ? "bg-primary text-primary-foreground"
                      : "bg-background text-foreground hover:bg-muted"
                  )}
                >
                  {isRTL ? "אוטומטית" : "Auto"}
                </button>
                <button
                  onClick={() => setLayoutMode("flower")}
                  className={cn(
                    "px-3 py-1.5 text-sm font-medium transition-colors",
                    layoutMode === "flower"
                      ? "bg-primary text-primary-foreground"
                      : "bg-background text-foreground hover:bg-muted"
                  )}
                >
                  {isRTL ? "מחוות" : "Gestures"}
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleUndo}
              disabled={undoHistory.length === 0}
            >
              <Undo2 className="w-4 h-4 mr-2" />
              {t("undo")}
            </Button>
            
            <Button variant="outline" size="sm" onClick={handleResetToDefault}>
              <RotateCcw className="w-4 h-4 mr-2" />
              {t("resetToDefault")}
            </Button>
            
            <Button size="sm" disabled={undoHistory.length === 0} onClick={() => {
              window.dispatchEvent(new Event('navigationBoardUpdated'));
              navigate('/board-editor');
            }}>
              <Check className="w-4 h-4 mr-2" />
              {t("doneEditing")}
            </Button>
          </div>
        </div>

        {/* Items grid - no scrolling, matches HomeGrid sizing */}
        <div className="flex-1 flex items-center justify-center p-2 min-h-0">
          {layoutMode === "row" ? renderRowLayout() : renderFlowerLayout()}
        </div>
      </div>
    </MainLayout>
  );
}
